import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { highlightIllegalXmlChars, noIllegalXmlCharsValidator, scrollToIllegalXmlChar } from 'src/app/shared/validators/xml-validators';
import { ObservationService } from 'src/app/services/observation.service';
import { ProgramChecklistService } from 'src/app/services/program-checklist.service';
import { AssessmentOption } from 'src/app/shared/models/assessmentOption';
import { FsObservation } from 'src/app/shared/models/fs-observation.model';

@Component({
  standalone: false,
  selector: 'app-obs-summary',
  templateUrl: './obs-summary.component.html',
  styleUrls: ['./obs-summary.component.scss']
})
export class ObsSummaryComponent implements OnInit {
  @ViewChild('pendingRequirementsTextarea') pendingRequirementsTextarea: ElementRef;
  @ViewChild('strengthsTextarea') strengthsTextarea: ElementRef;
  @ViewChild('areasOfGrowthTextarea') areasOfGrowthTextarea: ElementRef;
  @ViewChild('actionableStepsTextarea') actionableStepsTextarea: ElementRef;
  @ViewChild('otherNotesTextarea') otherNotesTextarea: ElementRef;
  @Input() obsAssessmentForm: FormGroup;
  @Input() fsObservation: FsObservation;
  @Input() completed: boolean;
  @Input() profileId: string;
  @Input() assessmentId: any;
  obsSummaryForm: FormGroup;
  finishing: boolean;
  overallAssessmentOptions: Array<AssessmentOption>;
  isPathwayEnabled = false;

  constructor(private observationService: ObservationService,
    private fb: FormBuilder,
    private programChecklistService: ProgramChecklistService) { }

  ngOnInit() {
    this.initializeFormGroup();
    this.getOverallAssessmentOptions();
    this.getPendingRequirements();
  }

  get showReloadButton() {
    return !this.completed && this.isPathwayEnabled;
  }

  private initializeFormGroup() {
    this.obsSummaryForm = this.fb.group({
      pendingRequirements: [this.fsObservation.pendingRequirements ?? '', [this.requiredOnFinishValidator.bind(this), noIllegalXmlCharsValidator]],
      strengths: [this.fsObservation.strengths ?? '', [Validators.maxLength(500), this.requiredOnFinishValidator.bind(this), noIllegalXmlCharsValidator]],
      areasOfGrowth: [this.fsObservation.areasOfGrowth ?? '', [Validators.maxLength(500), this.requiredOnFinishValidator.bind(this), noIllegalXmlCharsValidator]],
      actionableSteps: [this.fsObservation.actionableSteps ?? '', [Validators.maxLength(1500), this.requiredOnFinishValidator.bind(this), noIllegalXmlCharsValidator]],
      otherNotes: [this.fsObservation.otherNotes ?? '', [Validators.maxLength(1000), this.requiredOnFinishValidator.bind(this), noIllegalXmlCharsValidator]],
      overallAssessmentId: [this.fsObservation.overallAssessmentId ?? 0, this.overallAssessmentValidator.bind(this)]
    });
    this.obsAssessmentForm.addControl('obsSummary', this.obsSummaryForm);

    if (this.completed) {
      this.obsSummaryForm.disable();
    }
  }

  ngOnDestroy() {
    // Remove the form control from the parent form so that the child form controls are 
    // successfully reinitialized when the form is reloaded after being hidden.
    this.obsAssessmentForm.removeControl('obsSummary');
  }

  getOverallAssessmentOptions() {
    this.observationService.GetAssessmentTypeOptions().subscribe(data => {
      this.overallAssessmentOptions = data;
    });
  }

  validateForm(isFinishing: boolean = false): string[] {
    let errors = [];
    this.finishing = isFinishing;
    this.markFormGroupsAsTouched(this.obsSummaryForm);

    this.updateValidators();

    if (this.finishing && this.obsSummaryForm.invalid) {
      const errorMessages = {
        pendingRequirements: this.getErrorMessage('pendingRequirements'),
        strengths: this.getErrorMessage('strengths'),
        areasOfGrowth: this.getErrorMessage('areasOfGrowth'),
        actionableSteps: this.getErrorMessage('actionableSteps'),
        otherNotes: this.getErrorMessage('otherNotes'),
        overallAssessmentId: this.getErrorMessage('overallAssessmentId')
      };

      for (const controlName in errorMessages) {
        if (this.obsSummaryForm.get(controlName).errors) {
          errors.push(errorMessages[controlName]);
        }
      }
    } else {
      this.finishing = false;
    }

    for (const controlName of ['pendingRequirements', 'strengths', 'areasOfGrowth', 'actionableSteps', 'otherNotes']) {
      if (this.obsSummaryForm.get(controlName).errors?.illegalXmlChars) {
        errors.push(this.getErrorMessage(controlName));
      }
    }

    return errors;
  }

  updateValidators() {
    this.obsSummaryForm.get('pendingRequirements').updateValueAndValidity();
    this.obsSummaryForm.get('strengths').updateValueAndValidity();
    this.obsSummaryForm.get('areasOfGrowth').updateValueAndValidity();
    this.obsSummaryForm.get('actionableSteps').updateValueAndValidity();
    this.obsSummaryForm.get('otherNotes').updateValueAndValidity();
    this.obsSummaryForm.get('overallAssessmentId').updateValueAndValidity();
  }

  markFormGroupsAsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({ onlySelf: true });
      if (control instanceof FormGroup) {
        this.markFormGroupsAsTouched(control);
      }
    });
    formGroup.markAsTouched();
  }

  requiredOnFinishValidator(control: AbstractControl): ValidationErrors | null {
    if (this.finishing && !control.value) {
      return { required: true };
    }
    return null;
  }

  overallAssessmentValidator(control: AbstractControl): ValidationErrors | null {
    if (this.finishing && control.value === 0) {
      return { min: true };
    }
    return null;
  }


  scrollToFirstFieldError(): Promise<boolean> {
    const fields = [
      { name: 'pendingRequirements', ref: this.pendingRequirementsTextarea },
      { name: 'strengths', ref: this.strengthsTextarea },
      { name: 'areasOfGrowth', ref: this.areasOfGrowthTextarea },
      { name: 'actionableSteps', ref: this.actionableStepsTextarea },
      { name: 'otherNotes', ref: this.otherNotesTextarea },
    ];
    for (const field of fields) {
      const control = this.obsSummaryForm.get(field.name);
      if (control?.errors && field.ref) {
        if (control.errors.illegalXmlChars) {
          highlightIllegalXmlChars(field.ref.nativeElement, control.value);
        } else {
          field.ref.nativeElement.scrollIntoView({ block: 'center' });
        }
        return Promise.resolve(true);
      }
    }
    return Promise.resolve(false);
  }

  onFieldBlur(controlName: string): void {
    const control = this.obsSummaryForm.get(controlName);
    if (!control?.errors?.illegalXmlChars) {
      return;
    }
    const refs: Record<string, ElementRef> = {
      pendingRequirements: this.pendingRequirementsTextarea,
      strengths: this.strengthsTextarea,
      areasOfGrowth: this.areasOfGrowthTextarea,
      actionableSteps: this.actionableStepsTextarea,
      otherNotes: this.otherNotesTextarea,
    };
    const ref = refs[controlName];
    if (ref) {
      scrollToIllegalXmlChar(ref.nativeElement, control.value);
    }
  }

  getErrorMessage(controlName: string): string {
    const control = this.obsSummaryForm.get(controlName);
    if (control.errors) {
      switch (controlName) {
        case 'pendingRequirements':
          if (control.errors.required) return 'Pending Program Requirements is required';
          if (control.errors.illegalXmlChars) return 'Pending Program Requirements contains unknown characters';
          break;
        case 'strengths':
          if (control.errors.required) return 'Strengths is required';
          if (control.errors.illegalXmlChars) return 'Strengths contains unknown characters';
          break;
        case 'areasOfGrowth':
          if (control.errors.required) return 'Areas of Growth is required';
          if (control.errors.illegalXmlChars) return 'Areas of Growth contains unknown characters';
          break;
        case 'actionableSteps':
          if (control.errors.required) return 'Actionable Steps is required';
          if (control.errors.illegalXmlChars) return 'Actionable Steps contains unknown characters';
          break;
        case 'otherNotes':
          if (control.errors.required) return 'Other Notes is required';
          if (control.errors.illegalXmlChars) return 'Other Notes contains unknown characters';
          break;
        case 'overallAssessmentId':
          if (control.errors.required) return 'An Overall Assessment is required';
          if (control.errors.min) return 'Overall Assessment is required';
          break;
      }
    }
    return '';
  }

  getPendingRequirements(isManualLoad: boolean = false) {
    this.programChecklistService
      .getProgramChecklist(Number(this.profileId))
      .subscribe(checklist => {
        if (checklist == null || checklist.length == 0) {
          return;
        }

        this.isPathwayEnabled = true;
        if (this.assessmentId && !isManualLoad) {
          this.adjustTextareaHeight();
          return;
        }

        const pendingRequirements: string[] = [];
        checklist.forEach(group => {
          if (group.isCompleted) {
            return;
          }

          group.programChecklistStates.forEach(item => {
            if (item.isComplete) {
              return;
            }

            let bulletPoint: string = "\u2022";
            let pendingRequirement = `${bulletPoint} ${item.name}`;

            if (item.progressDetails) {
              const { progress, fullProgress, progressMessage } = item.progressDetails;
              const progressText = progressMessage
                .replace('{{Progress}}', progress.toString())
                .replace('{{FullProgress}}', fullProgress.toString());

              pendingRequirement += ` (${progressText})`;
            }

            pendingRequirements.push(pendingRequirement);
          });
        });
        this.fsObservation.pendingRequirements = pendingRequirements.join('\n');
        this.obsSummaryForm.get('pendingRequirements').setValue(this.fsObservation.pendingRequirements);
        this.adjustTextareaHeight();
      });
  }

  adjustTextareaHeight(): void {
    const textarea = this.pendingRequirementsTextarea.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }
}
